import { useEffect, useRef } from 'react';
import { Field, FieldRenderProps } from 'react-final-form';
import { Compartment, EditorState } from '@codemirror/state';
import { EditorView, basicSetup } from 'codemirror';
import { placeholder as codemirrorPlaceholder } from '@codemirror/view';
import { xml } from '@codemirror/lang-xml';
import cn from 'classnames';

import styles from './XmlEditorField.module.scss';

interface IXmlEditorFieldProps {
  className?: string;
  disabled?: boolean;
  name: string;
  placeholder?: string;
}

const XmlEditor = ({
  className,
  disabled,
  input,
  placeholder,
}: {
  className?: string;
  disabled?: boolean;
  input: FieldRenderProps<string | undefined, HTMLElement>['input'];
  placeholder?: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView>();
  const editableCompartmentRef = useRef(new Compartment());
  const onChangeRef = useRef(input.onChange);

  onChangeRef.current = input.onChange;

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const editableCompartment = editableCompartmentRef.current;
    const view = new EditorView({
      parent: containerRef.current,
      state: EditorState.create({
        doc: input.value || '',
        extensions: [
          basicSetup,
          xml(),
          EditorView.lineWrapping,
          ...(placeholder ? [codemirrorPlaceholder(placeholder)] : []),
          EditorView.updateListener.of(update => {
            if (update.docChanged) {
              onChangeRef.current(update.state.doc.toString());
            }
          }),
          editableCompartment.of([EditorState.readOnly.of(!!disabled), EditorView.editable.of(!disabled)]),
        ],
      }),
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = undefined;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const view = viewRef.current;

    if (!view) {
      return;
    }

    const nextValue = input.value || '';
    const currentValue = view.state.doc.toString();

    if (nextValue !== currentValue) {
      view.dispatch({
        changes: {
          from: 0,
          to: currentValue.length,
          insert: nextValue,
        },
      });
    }
  }, [input.value]);

  useEffect(() => {
    const view = viewRef.current;

    if (!view) {
      return;
    }

    view.dispatch({
      effects: editableCompartmentRef.current.reconfigure([
        EditorState.readOnly.of(!!disabled),
        EditorView.editable.of(!disabled),
      ]),
    });
  }, [disabled]);

  return (
    <div
      ref={containerRef}
      className={cn(styles.XmlEditor, className, {
        [styles.XmlEditorDisabled]: disabled,
      })}
    />
  );
};

export const XmlEditorField = ({ className, disabled, name, placeholder }: IXmlEditorFieldProps) => {
  return (
    <Field name={name} subscription={{ value: true }}>
      {({ input }) => <XmlEditor className={className} disabled={disabled} input={input} placeholder={placeholder} />}
    </Field>
  );
};
